import React, { PureComponent, createRef } from "react";
import Highcharts from "highcharts";
import axios from "axios";
import config from "../config";
import DatePicker from "react-datepicker";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";
import SearchableSelect from "./SearchableSelect";

const CHART_KEYS = [
  { key: "ip", title: "IP" },
  { key: "isp", title: "ISP" },
  { key: "browser", title: "Browser" },
  { key: "device", title: "Device" },
  { key: "country", title: "Country" },
  { key: "city", title: "City" },
];

/**
 * View Insights → POST /analytic/view/insights
 * Pie charts + channel/range filters + Export CSV
 */
export default class InsightsCharts extends PureComponent {
  charts = {};
  chartRefs = {};

  constructor(props) {
    super(props);

    CHART_KEYS.forEach(({ key }) => {
      this.chartRefs[key] = createRef();
    });

    this.state = {
      channels: [],
      startDate: moment().startOf("day").toDate(),
      endDate: moment().endOf("day").toDate(),
      range: "today",
      typeFilter: "all",
      channel: "0",
      loading: false,
      hasData: false,
      insightData: null,
    };
  }

  componentDidMount() {
    // Wait for refs to attach before creating charts
    this.initTimer = window.setTimeout(() => {
      this.renderPieCharts({});
      this.feedChannel();
    }, 0);
  }

  componentWillUnmount() {
    this._unmounted = true;
    if (this.initTimer) {
      window.clearTimeout(this.initTimer);
      this.initTimer = null;
    }
    this.destroyCharts();
  }

  toEpochMs = (date) => Number(moment(date).valueOf());

  getStreamId = (item) => item?.id || item?._id || "";

  getService = (item) =>
    String(item?.service || item?.type || "")
      .toLowerCase()
      .trim();

  isRadio = (item) => this.getService(item) === "radio";

  isLiveChannel = (item) => {
    const service = this.getService(item);
    return (
      service === "live" ||
      service === "channel" ||
      (!service && !this.isRadio(item))
    );
  };

  getVisibleChannels = () => {
    const { channels, typeFilter } = this.state;
    let filtered = channels || [];

    if (typeFilter === "radio") {
      filtered = filtered.filter((item) => this.isRadio(item));
    } else if (typeFilter === "channel") {
      filtered = filtered.filter((item) => this.isLiveChannel(item));
    }

    return filtered.sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
        sensitivity: "base",
      })
    );
  };

  /** Normalize Wowza / SMIL stream ids for matching display names. */
  normalizeStreamKey = (value) => {
    let s = String(value || "").trim();
    if (!s) return "";

    try {
      if (/^https?:\/\//i.test(s)) {
        const u = new URL(s);
        const parts = u.pathname.split("/").filter(Boolean);
        const smilPart = parts.find(
          (p) => /\.smil$/i.test(p) || /^smil:/i.test(p)
        );
        if (smilPart) {
          s = smilPart;
        } else if (parts.length >= 2 && /playlist\.m3u8$/i.test(parts[parts.length - 1])) {
          s = parts[parts.length - 2];
        } else if (parts.length) {
          s = parts[parts.length - 1];
        }
      }
    } catch (e) {}

    s = s.replace(/^smil:/i, "");
    s = s.replace(/\.smil$/i, "");
    s = s.replace(/\/?playlist\.m3u8$/i, "");
    s = s.replace(/\/?chunklist_[^/]+\.m3u8$/i, "");
    return s.toLowerCase();
  };

  getChannelStreamKeys = (item) =>
    [item?.stream_output, item?.stream, item?.streamkey, item?.stream_key]
      .filter(Boolean)
      .map(String);

  expandLookupKeys = (item) => {
    const raw = [
      item?.name,
      ...this.getChannelStreamKeys(item),
      item?.id,
      item?._id,
    ]
      .filter(Boolean)
      .map(String);

    const keys = new Set();
    raw.forEach((k) => {
      keys.add(k);
      const n = this.normalizeStreamKey(k);
      if (n) {
        keys.add(n);
        keys.add(`${n}.smil`);
        keys.add(`smil:${n}.smil`);
      }
    });
    return [...keys];
  };

  destroyCharts = () => {
    Object.keys(this.charts).forEach((key) => {
      if (this.charts[key]) {
        try {
          this.charts[key].destroy();
        } catch (e) {}
        this.charts[key] = null;
      }
    });
  };

  getChartEl = (key) => this.chartRefs[key] && this.chartRefs[key].current;

  pieOptions = (title, data) => ({
    chart: {
      type: "pie",
      backgroundColor: "transparent",
      spacing: [12, 8, 12, 8],
    },
    title: {
      text: title,
      align: "left",
      style: {
        fontSize: "0.95rem",
        fontWeight: "700",
        color: "#0f172a",
      },
    },
    credits: { enabled: false },
    tooltip: {
      pointFormat: "{series.name}: <b>{point.percentage:.1f}%</b> ({point.y})",
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format: "<b>{point.name}</b>: {point.percentage:.1f}%",
          style: {
            fontSize: "0.72rem",
            fontWeight: "600",
            color: "#475569",
            textOutline: "none",
          },
        },
      },
    },
    series: [
      {
        name: title,
        colorByPoint: true,
        data: Array.isArray(data) ? data : [],
      },
    ],
  });

  ensureChart = (key, title, data = []) => {
    const el = this.getChartEl(key);
    if (!el || !el.isConnected) return null;

    if (this.charts[key]) {
      try {
        this.charts[key].destroy();
      } catch (e) {}
      this.charts[key] = null;
    }

    try {
      this.charts[key] = Highcharts.chart(el, this.pieOptions(title, data));
      return this.charts[key];
    } catch (e) {
      this.charts[key] = null;
      return null;
    }
  };

  setChartsLoading = (isLoading, message = "Loading...") => {
    CHART_KEYS.forEach(({ key, title }) => {
      let chart = this.charts[key];
      if (!chart) {
        chart = this.ensureChart(key, title, []);
      }
      if (!chart) return;

      try {
        if (isLoading) {
          chart.showLoading(message);
        } else {
          chart.hideLoading();
        }
      } catch (e) {}
    });
  };

  renderPieCharts = (payload = {}, keepLoading = false) => {
    CHART_KEYS.forEach(({ key, title }) => {
      const chart = this.ensureChart(key, title, payload[key] || []);
      if (chart && keepLoading) {
        try {
          chart.showLoading("Loading...");
        } catch (e) {}
      }
    });
  };

  formatCategoryCell = (items) => {
    if (!items || !items.length) return "";
    return items.map((item) => `${item.name} (${item.y})`).join("; ");
  };

  resolveStreamDisplayName = (stream) => {
    const key = String(stream?.stream || stream?.name || "");
    const norm = this.normalizeStreamKey(key);

    const matched = (this.state.channels || []).find((ch) => {
      const keys = this.expandLookupKeys(ch);
      if (keys.includes(key)) return true;
      if (!norm) return false;
      return keys.some((k) => this.normalizeStreamKey(k) === norm);
    });

    // Prefer channel title — never leave bare *.smil when we can map it
    if (matched?.name) return matched.name;
    if (stream?.title) return stream.title;
    if (key && !/\.smil$/i.test(key) && !/^smil:/i.test(key)) {
      return stream?.name || key;
    }
    return matched?.name || stream?.name || key || "Channel";
  };

  escapeCsv = (value) => {
    const text = String(value ?? "");
    if (/[",\n]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  };

  buildCsvContent = (insightData) => {
    if (!insightData) return "";

    const { startDate, endDate, channel } = this.state;
    const start = moment(startDate).format("YYYY-MM-DD HH:mm");
    const end = moment(endDate).format("YYYY-MM-DD HH:mm");
    const isAll = channel === "0";
    const visible = this.getVisibleChannels();

    const header = [
      "Start",
      "End",
      "Channel",
      "IP",
      "ISP",
      "Browser",
      "Device",
      "Country",
      "City",
    ];

    const rowFromBreakdown = (name, data) => [
      start,
      end,
      name,
      this.formatCategoryCell(data.ip),
      this.formatCategoryCell(data.isp),
      this.formatCategoryCell(data.browser),
      this.formatCategoryCell(data.device),
      this.formatCategoryCell(data.country),
      this.formatCategoryCell(data.city),
    ];

    const rows = [header];
    const streams = Array.isArray(insightData.streams)
      ? insightData.streams
      : [];

    if (isAll && streams.length) {
      streams.forEach((stream) => {
        rows.push(
          rowFromBreakdown(this.resolveStreamDisplayName(stream), stream)
        );
      });
      rows.push(rowFromBreakdown("Total", insightData));
    } else if (isAll) {
      rows.push(rowFromBreakdown("Total", insightData));
    } else {
      const selected = visible.find(
        (item) => String(this.getStreamId(item)) === String(channel)
      );
      const name =
        selected?.name ||
        this.resolveStreamDisplayName(streams[0] || {}) ||
        "Channel";
      const source = streams[0] || insightData;
      rows.push(rowFromBreakdown(name, source));
    }

    return rows.map((row) => row.map(this.escapeCsv).join(",")).join("\n");
  };

  downloadCsv = (content, filename) => {
    const blob = new Blob(["\uFEFF" + content], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  exportCsv = () => {
    const { insightData, channel, startDate, endDate, hasData, typeFilter } =
      this.state;
    if (!hasData || !insightData) return;

    const visible = this.getVisibleChannels();
    const selected = visible.find(
      (item) => String(this.getStreamId(item)) === String(channel)
    );
    const typeLabel =
      typeFilter === "radio"
        ? "radio"
        : typeFilter === "channel"
          ? "channel"
          : "all";
    const label =
      channel === "0"
        ? `${typeLabel}-channels`
        : (selected?.name || "channel").replace(/\s+/g, "-").toLowerCase();
    const from = moment(startDate).format("YYYYMMDD-HHmm");
    const to = moment(endDate).format("YYYYMMDD-HHmm");
    const filename = `insights-${label}-${from}_${to}.csv`;

    this.downloadCsv(this.buildCsvContent(insightData), filename);
  };

  feedChannel = () => {
    axios
      .post(config.host + "/api/v1/live/stream", {
        userid: localStorage.StudioUserID,
      })
      .then((res) => {
        const channels = res.data.result || [];
        this.setState({ channels, channel: "0" }, () => {
          this.fetchInsights();
        });
      })
      .catch(() => {
        this.renderPieCharts({});
        this.setState({ hasData: false, insightData: null });
      });
  };

  fetchInsights = () => {
    const { startDate, endDate, channel } = this.state;
    const visible = this.getVisibleChannels();
    const selected = visible.find(
      (item) => String(this.getStreamId(item)) === String(channel)
    );

    const targetChannels =
      channel === "0" ? visible : selected ? [selected] : [];

    const uniqueStreamNames = [
      ...new Set(
        targetChannels.flatMap((item) => this.getChannelStreamKeys(item))
      ),
    ];

    if (!uniqueStreamNames.length) {
      this.renderPieCharts({});
      this.setState({ hasData: false, insightData: null, loading: false });
      return;
    }

    this.setState({ loading: true }, () => {
      this.setChartsLoading(true, "Loading...");
    });

    axios
      .post(
        config.host + "/analytic/view/insights",
        {
          start: this.toEpochMs(startDate),
          end: this.toEpochMs(endDate),
          id: channel || "0",
          customer: localStorage.StudioUserID || "0",
          streamname: uniqueStreamNames,
        },
        { timeout: 60000 }
      )
      .then((res) => {
        if (this._unmounted) return;
        const data = res.data || {};
        const hasData = CHART_KEYS.some(
          ({ key }) => Array.isArray(data[key]) && data[key].length > 0
        );
        this.setState({ hasData, insightData: hasData ? data : null }, () => {
          this.renderPieCharts(data);
          this.setChartsLoading(false);
        });
      })
      .catch(() => {
        if (this._unmounted) return;
        this.setState({ hasData: false, insightData: null }, () => {
          this.renderPieCharts({});
          this.setChartsLoading(false);
        });
      })
      .then(() => {
        if (this._unmounted) return;
        this.setState({ loading: false });
      });
  };

  handleChangeRange = (event) => {
    const range = event.target.value;
    let startDate = this.state.startDate;
    let endDate = this.state.endDate;

    switch (range) {
      case "today":
        startDate = moment().startOf("day").toDate();
        endDate = moment().endOf("day").toDate();
        break;
      case "lasthour":
        startDate = moment().subtract(1, "hours").toDate();
        endDate = moment().toDate();
        break;
      case "last4hours":
        startDate = moment().subtract(4, "hours").toDate();
        endDate = moment().toDate();
        break;
      case "last8hours":
        startDate = moment().subtract(8, "hours").toDate();
        endDate = moment().toDate();
        break;
      case "last12hours":
        startDate = moment().subtract(12, "hours").toDate();
        endDate = moment().toDate();
        break;
      case "yesterday":
        startDate = moment().subtract(1, "days").startOf("day").toDate();
        endDate = moment().subtract(1, "days").endOf("day").toDate();
        break;
      case "last7days":
        startDate = moment().subtract(6, "days").startOf("day").toDate();
        endDate = moment().endOf("day").toDate();
        break;
      case "thismonth":
        startDate = moment().startOf("month").toDate();
        endDate = moment().endOf("month").toDate();
        break;
      case "lastmonth":
        startDate = moment().subtract(1, "month").startOf("month").toDate();
        endDate = moment().subtract(1, "month").endOf("month").toDate();
        break;
      case "thisyear":
        startDate = moment().startOf("year").toDate();
        endDate = moment().endOf("year").toDate();
        break;
      case "lastyear":
        startDate = moment().subtract(1, "year").startOf("year").toDate();
        endDate = moment().subtract(1, "year").endOf("year").toDate();
        break;
      default:
        break;
    }

    this.setState({ range, startDate, endDate });
  };

  handleChangeChannel = (event) => {
    this.setState({ channel: event.target.value });
  };

  handleChangeType = (event) => {
    this.setState({ typeFilter: event.target.value, channel: "0" });
  };

  searchData = () => {
    this.fetchInsights();
  };

  render() {
    const visibleChannels = this.getVisibleChannels();

    return (
      <div className="insights-wrap">
        <div className="monitoring-filters">
          <div className="monitoring-field">
            <label className="monitoring-label">Type</label>
            <select
              className="monitoring-input"
              value={this.state.typeFilter}
              onChange={this.handleChangeType}
            >
              <option value="all">All</option>
              <option value="channel">Channel</option>
              <option value="radio">Radio</option>
            </select>
          </div>

          <SearchableSelect
            label="Channels"
            value={this.state.channel}
            onChange={this.handleChangeChannel}
            options={visibleChannels.map((item) => ({
              value: this.getStreamId(item),
              label: item.name,
            }))}
          />

          <div className="monitoring-field">
            <label className="monitoring-label">Date Range</label>
            <select
              className="monitoring-input"
              onChange={this.handleChangeRange}
              value={this.state.range}
            >
              <option value="today">Today</option>
              <option value="lasthour">Last Hour</option>
              <option value="last4hours">Last 4 Hours</option>
              <option value="last8hours">Last 8 Hours</option>
              <option value="last12hours">Last 12 Hours</option>
              <option value="yesterday">Yesterday</option>
              <option value="last7days">Last 7 Days</option>
              <option value="thismonth">This Month</option>
              <option value="lastmonth">Last Month</option>
              <option value="thisyear">This Year</option>
              <option value="lastyear">Last Year</option>
            </select>
          </div>

          <div className="monitoring-field">
            <label className="monitoring-label">From</label>
            <DatePicker
              selected={this.state.startDate}
              onChange={(date) => this.setState({ startDate: date })}
              timeInputLabel="Time:"
              showTimeInput
              dateFormat="yyyy-MM-dd  HH:mm"
              className="monitoring-input"
            />
          </div>

          <div className="monitoring-field">
            <label className="monitoring-label">To</label>
            <DatePicker
              selected={this.state.endDate}
              onChange={(date) => this.setState({ endDate: date })}
              timeInputLabel="Time:"
              showTimeInput
              dateFormat="yyyy-MM-dd  HH:mm"
              className="monitoring-input"
            />
          </div>

          <div className="monitoring-field monitoring-field--actions">
            <button
              type="button"
              className="monitoring-search-btn"
              onClick={this.searchData}
              disabled={this.state.loading}
            >
              {this.state.loading ? "Loading..." : "Search"}
            </button>
            <button
              type="button"
              className="monitoring-export-btn"
              onClick={this.exportCsv}
              disabled={this.state.loading || !this.state.hasData}
            >
              Export CSV
            </button>
          </div>
        </div>

        {!this.state.loading && !this.state.hasData ? (
          <p className="insights-empty">No insight data for this selection.</p>
        ) : null}

        <div className="insights-charts-grid">
          {CHART_KEYS.map(({ key }) => (
            <div key={key} className="insights-chart-panel">
              <div ref={this.chartRefs[key]} className="insights-chart-host" />
            </div>
          ))}
        </div>
      </div>
    );
  }
}
